import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { StripeInvoice } from '@/types/stripe';
import { formatStripeAmount, formatStripeDate } from '@/utils/stripeFormatters';

const INVOICE_STATUS_CLASS: Record<string, string> = {
  paid: 'bg-green-100 text-green-800 border-green-200',
  open: 'bg-amber-100 text-amber-800 border-amber-200',
  void: 'bg-gray-100 text-gray-700 border-gray-200',
  uncollectible: 'bg-red-100 text-red-800 border-red-200',
};

const INVOICE_STATUS_LABEL_KEY: Record<string, string> = {
  paid: 'invoicesTable.statusPaid',
  open: 'invoicesTable.statusOpen',
  void: 'invoicesTable.statusVoid',
  uncollectible: 'invoicesTable.statusUncollectible',
};

interface StripeInvoicesTableProps {
  invoices?: StripeInvoice[];
}

export function StripeInvoicesTable({ invoices }: StripeInvoicesTableProps) {
  const { t } = useTranslation('billing');

  function getInvoiceStatusBadge(status: string | null | undefined) {
    if (!status) return <span className="text-gray-500">—</span>;
    const key = status.toLowerCase();
    const className = INVOICE_STATUS_CLASS[key] ?? 'bg-gray-100 text-gray-800 border-gray-200';
    const labelKey = INVOICE_STATUS_LABEL_KEY[key];
    const label = labelKey ? t(labelKey) : status;
    return <Badge variant="outline" className={className}>{label}</Badge>;
  }

  if (!invoices || invoices.length === 0) {
    return <div className="text-sm text-gray-600">{t('invoicesTable.noData')}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2 sm:hidden">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="font-medium text-gray-900">{invoice.number || invoice.id}</div>
            <div className="flex items-center gap-2 mt-1">
              {getInvoiceStatusBadge(invoice.status)}
              <span className="text-xs text-gray-500">{formatStripeDate(invoice.created)}</span>
            </div>
            <div className="text-sm text-gray-700 mt-2">
              {formatStripeAmount(invoice.amount_paid, invoice.currency)} {t('invoicesTable.statusPaid')}
            </div>
            <div className="flex flex-wrap gap-3 mt-2">
              {invoice.hosted_invoice_url && (
                <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">
                  {t('invoicesTable.view')} <ArrowTopRightOnSquareIcon width={12} height={12} />
                </a>
              )}
              {invoice.invoice_pdf && (
                <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">
                  {t('invoicesTable.pdf')} <ArrowDownTrayIcon width={12} height={12} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('invoicesTable.date')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('invoicesTable.number')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('invoicesTable.status')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('invoicesTable.paid')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('invoicesTable.due')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('invoicesTable.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatStripeDate(invoice.created)}</td>
                <td className="px-3 py-2 text-gray-900">{invoice.number || invoice.id}</td>
                <td className="px-3 py-2">{getInvoiceStatusBadge(invoice.status)}</td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatStripeAmount(invoice.amount_paid, invoice.currency)}</td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatStripeAmount(invoice.amount_due, invoice.currency)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-3">
                    {invoice.hosted_invoice_url && (
                      <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">{t('invoicesTable.view')} <ArrowTopRightOnSquareIcon width={12} height={12} /></a>
                    )}
                    {invoice.invoice_pdf && (
                      <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">{t('invoicesTable.pdf')} <ArrowDownTrayIcon width={12} height={12} /></a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

